import {
  ErrorOutline,
  Nightlight,
  WbSunny
} from "@mui/icons-material";
import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { GET_SINGLE_THERAPY_DAY_BY_DATE } from "../../../../../../enums/ApiPaths";
import { useFetch } from "../../../../../../hooks/Fetching";
import AddOrRemoveTherapiesDialog from "./form/AddOrRemoveTherapiesDialog";
import EditTherapyDayDialog from "./form/EditTherapyDayDialog";
import { CustomDay } from "./TherapySchedule";
import { getEarliestDate } from "../../../../../../utils/DateTimeRepository";

export default function EditTherapyDay({ dates = [], days = []}) {
  return (
    <Fragment>
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <CalendarComponent dates={dates} days={days} />
        </Grid>
        <Grid item xs={12} md={8}>
          <TableComponent />
        </Grid>
      </Grid>
    </Fragment>
  );
}

function CalendarComponent({ dates = [], days = [] }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  function handleSetParam(value) {
    searchParams.set("edit_day_date", dayjs(value).format("DD/MM/YYYY"));
    setSearchParams(searchParams);
  }

  function getDateFromParams(date) {
    const formattedDate = dayjs(date).format("DD/MM/YYYY");
    return String(searchParams.get("edit_day_date")) === String(formattedDate);
  }

  function handleDisableDates(date) {
    return !dates.includes(dayjs(date).format("DD/MM/YYYY"));
  }

  if (!dates || dates.length == 0) {
    return <NoDataView text="NoDataToShowInCalendar" />;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack direction={"row"} justifyContent={"space-between"} mb={3}>
        <Typography variant="h6" fontWeight="500" color="text.primary">
          {t("TherapyDaysCalendar")}
        </Typography>
        <AddOrRemoveTherapiesDialog dates={dates} days={days}/>
      </Stack>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <DateCalendar
          referenceDate={getEarliestDate(dates, "DD/MM/YYYY")}
          shouldDisableDate={handleDisableDates}
          onChange={(value) => handleSetParam(value)}
          sx={{
            "& .MuiPickersDay-root": {
              fontSize: "0.875rem",
            },
          }}
          slots={{
            day: (props) => {
              return (
                <CustomDay
                  {...props}
                  selected={getDateFromParams(props.day)}
                  isGreen={
                    true &&
                    dates.includes(dayjs(props.day).format("DD/MM/YYYY"))
                  }
                />
              );
            },
          }}
        />
      </Box>
      <Legend />
    </Paper>
  );
}

function Legend() {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", gap: 3, justifyContent: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: "success.main",
          }}
        />
        <Typography variant="caption">{t("Available")}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: "primary.main",
          }}
        />
        <Typography variant="caption">{t("Selected")}</Typography>
      </Box>
    </Box>
  );
}

function TableComponent() {
  const { t } = useTranslation();
  const { data, error, isLoading } = useGetTherapy();

  if (isLoading) {
    return <LinearProgress />;
  } else if (error) {
    return <ErrorView />;
  } else if (!data || data.length == 0) {
    return <NoDataView />;
  }

  return (
    <ViewerWrapper>
      <Stack
        mb={3}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("Date")}
          </Typography>
          <Typography variant="h5" fontWeight={500}>
            {data.date || "N/A"}
          </Typography>
        </Box>
        <EditTherapyDayDialog therapyDay={data} />
      </Stack>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            bgcolor: "rgba(255, 193, 7, 0.08)",
            border: "1px solid",
            borderColor: "rgba(255, 193, 7, 0.2)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <WbSunny sx={{ color: "warning.main", mr: 1 }} />
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
            >
              {t("MorningDose")}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
            {data.morningDosage || "N/A"}
          </Typography>
        </Box>

        {/* Evening Dose */}
        <Box
          sx={{
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            bgcolor: "rgba(33, 150, 243, 0.08)",
            border: "1px solid",
            borderColor: "rgba(33, 150, 243, 0.2)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Nightlight sx={{ color: "info.main", mr: 1 }} />
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
            >
              {t("EveningDose")}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
            {data.eveningDosage || "N/A"}
          </Typography>
        </Box>
      </Box>
    </ViewerWrapper>
  );
}

function ViewerWrapper({ children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      {children}
    </Paper>
  );
}

function NoDataView({ text = "SelectDateToViewTherapyDay" }) {
  const { t } = useTranslation();

  return (
    <ViewerWrapper>
      <Stack alignItems={"center"} justifyContent={"center"}>
        <Typography color={"GrayText"} variant="h5">
          {t(text)}
        </Typography>
      </Stack>
    </ViewerWrapper>
  );
}

function ErrorView() {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack alignItems={"center"} justifyContent={"center"} height={"100%"}>
        <ErrorOutline color="error" fontSize="large" />
        <Typography align="center" variant="h6" color="error">
          {t("SomethingWentWrong")}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {t("UnableToLoadTherapyDayValues")}
        </Typography>
      </Stack>
    </Paper>
  );
}

export const editDateParam = "edit_day_date";

function useGetTherapy() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  var date = searchParams.get(editDateParam) ?? "";

  if (String(date) === "Invalid Date") {
    date = "";
  }

  let apiPath = `${GET_SINGLE_THERAPY_DAY_BY_DATE}${id}&date=${date}`;

  if (date == "" || date == null) {
    apiPath = null;
  }

  const { data, isLoading, error } = useFetch(apiPath, null, [
    editDateParam,
    date,
  ]);

  return { data, isLoading, error };
}

